// src/components/StateManagement.jsx
import React, { useEffect, useState } from "react";
import axios from "axios";
import { BASE_URL } from "../config";
import { getAuthHeaders } from "../services/useAuth";
import CustomButton from "../pages/CustomButton";

const COLUMNS = [
  { key: "headquarters_count", label: "Headquarters", path: "/headquarters" },
  { key: "employees_count", label: "Employees", path: "/employees" },
  { key: "doctors_count", label: "Doctors", path: "/doctors" },
  { key: "chemists_count", label: "Chemists", path: "/chemists" },
  { key: "cities_count", label: "Cities", path: "/cities" },
];

const StateManagement = () => {
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingState, setEditingState] = useState(null);
  const [stateName, setStateName] = useState("");
  const [isAddModalOpen, setAddModalOpen] = useState(false);
  const [newStateName, setNewStateName] = useState("");
  const [newCountry, setNewCountry] = useState("India");

  useEffect(() => {
    fetchStates();
  }, []);

  const normalizeState = (s) => {
    const counts = s.counts ?? s.summary ?? {};
    return {
      id: s.id ?? s._id ?? s.code ?? null,
      name: s.name ?? s.state_name ?? s.title ?? "Unknown",
      headquarters_count:
        s.headquarters_count ??
        s.headquarter_count ??
        s.hq_count ??
        counts.headquarters ??
        counts.hq ??
        s.headquarters ??
        0,
      employees_count:
        s.employees_count ??
        s.employee_count ??
        counts.employees ??
        counts.employee ??
        s.employees ??
        0,
      doctors_count:
        s.doctors_count ??
        s.doctor_count ??
        counts.doctors ??
        counts.doctor ??
        s.doctors ??
        0,
      chemists_count:
        s.chemists_count ??
        s.chemist_count ??
        counts.chemists ??
        s.chemists ??
        0,
      cities_count:
        s.cities_count ??
        s.city_count ??
        counts.cities ??
        s.cities ??
        0,
      raw: s,
    };
  };

  // Fetch states from API and normalize
  const fetchStates = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BASE_URL}/api/state-summary`, {
        headers: getAuthHeaders(),
      });

      console.log("States fetched (raw):", response.data);

      let payload = response.data;

      if (Array.isArray(payload)) {
        setStates(payload.map(normalizeState));
      } else if (payload && Array.isArray(payload.states)) {
        setStates(payload.states.map(normalizeState));
      } else if (payload && Array.isArray(payload.data)) {
        setStates(payload.data.map(normalizeState));
      } else if (payload && typeof payload === "object") {
        const values = Object.values(payload);
        if (values.length === 1 && Array.isArray(values[0])) {
          setStates(values[0].map(normalizeState));
        } else if (values.length > 0 && typeof values[0] === "object") {
          setStates(values.map(normalizeState));
        } else {
          setStates([]);
        }
      } else {
        setStates([]);
      }
    } catch (error) {
      console.error("Error fetching states:", error);
      alert("Failed to fetch states");
      setStates([]);
    } finally {
      setLoading(false);
    }
  };

  // Editing handlers
  const startEditing = (state) => {
    setEditingState(state);
    setStateName(state.name ?? "");
  };

  const cancelEditing = () => {
    setEditingState(null);
    setStateName("");
  };

  const handleUpdateState = async (id) => {
    if (!stateName.trim() || !editingState) {
      alert("Please enter a state name");
      return;
    }
    setSaving(true);
    try {
      const url = `${BASE_URL}/api/states/${id}`;
      const payload = {
        name: stateName.trim(),
      };
      const response = await axios.put(url, payload, {
        headers: getAuthHeaders(),
      });
      if (response.status === 200 || response.status === 204) {
        await fetchStates();
        alert("State updated successfully");
        cancelEditing();
      } else {
        console.warn("Unexpected update response", response);
        alert("Failed to update state");
      }
    } catch (err) {
      console.error("Error updating state:", err);
      alert("Failed to update state");
    } finally {
      setSaving(false);
    }
  };

  // Delete
  const handleDeleteState = async (id) => {
    if (!window.confirm("Are you sure you want to delete this state?")) return;
    setSaving(true);
    try {
      const url = `${BASE_URL}/api/states/${id}`;
      const response = await axios.delete(url, {
        headers: getAuthHeaders(),
      });
      if (response.status === 200 || response.status === 204) {
        await fetchStates();
        alert("State deleted successfully");
      } else {
        console.warn("Unexpected delete response", response);
        alert("Failed to delete state");
      }
    } catch (err) {
      console.error("Error deleting state:", err);
      alert("Failed to delete state");
    } finally {
      setSaving(false);
    }
  };

  // Add
  const handleAddState = async (e) => {
    e && e.preventDefault();
    if (!newStateName.trim()) {
      alert("Please enter a state name");
      return;
    }
    setSaving(true);
    try {
      const url = `${BASE_URL}/api/states`;
      const payload = {
        name: newStateName.trim(),
        country: newCountry,
      };
      const response = await axios.post(url, payload, {
        headers: getAuthHeaders(),
      });
      if (response.status === 201 || response.status === 200) {
        setNewStateName("");
        setNewCountry("India");
        setAddModalOpen(false);
        await fetchStates();
        alert("State added successfully");
      } else {
        console.warn("Unexpected add response", response);
        alert("Failed to add state");
      }
    } catch (err) {
      console.error("Error adding state:", err);
      alert("Failed to add state");
    } finally {
      setSaving(false);
    }
  };

  // Helper to get value with fallbacks
  const renderCellValue = (state, colKey) => {
    const normalized = state[colKey];
    if (normalized !== undefined && normalized !== null) return normalized;
    return state.raw?.counts?.[colKey.replace("_count", "")] ?? state.raw?.[colKey] ?? 0;
  };

  // Build href for a count cell
  const buildHref = (path, state) => {
    const id = encodeURIComponent(state.id ?? "");
    const name = encodeURIComponent(state.name ?? "");
    return `${path}?state=${id}&stateName=${name}`;
  };

  return (
    <div className="flex flex-col border border-gray-200">
      <main className="flex-1 p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-xl sm:text-2xl font-bold text-center text-gray-800 mb-4">
            <span className="text-blue-700"> State </span>
            <span className="text-blue-500">Management</span>
          </h1>

          <CustomButton
            onClick={() => setAddModalOpen(true)}
            variant="Add"
            className="bg-blue-500 text-white px-4 py-2 rounded"
            label={"Add"}
          />
        </div>

        {/* Table */}
       <div className="border rounded shadow  table-wrapper">
        <table className="unlock-dcr-leave-table text-sm ">
          <thead className="bg-blue-500 text-white">
              <tr>
                <th className="border p-2">S.No</th>
                <th className="border p-2 text-left">Name</th>
                {COLUMNS.map((c) => (
                  <th key={c.key} className="border p-2">
                    {c.label}
                  </th>
                ))}
                <th className="border p-2">Actions</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={3 + COLUMNS.length} className="text-center p-4">
                    Loading...
                  </td>
                </tr>
              ) : states.length === 0 ? (
                <tr>
                  <td colSpan={3 + COLUMNS.length} className="text-center p-4">
                    No states found
                  </td>
                </tr>
              ) : (
                states.map((state, index) => (
                  <tr key={state.id ?? index} className="text-center">
                    <td className="border p-2">{index + 1}</td>

                    <td className="border p-2 text-left">
                      {editingState && editingState.id === state.id ? (
                        <input
                          type="text"
                          value={stateName}
                          onChange={(e) => setStateName(e.target.value)}
                          className="border p-1 w-full"
                        />
                      ) : (
                        state.name
                      )}
                    </td>

                    {COLUMNS.map((c) => {
                      const countValue = renderCellValue(state, c.key) ?? 0;
                      const href = buildHref(c.path, state);

                      return (
                        <td key={c.key} className="border p-2">
                          <a
                            href={href}
                            className="text-blue-600 underline"
                            title={`View ${c.label} for ${state.name}`}
                          >
                            {countValue}
                          </a>
                        </td>
                      );
                    })}

                    <td className="border p-2">
                      {editingState && editingState.id === state.id ? (
                        <>
                          <CustomButton
                            onClick={() => handleUpdateState(state.id)}
                            variant="Save"
                            className="bg-green-500 text-white px-4 py-2 rounded"
                            label={saving ? "Saving..." : "Save"}
                            disabled={saving}
                          />
                          <CustomButton
                            onClick={cancelEditing}
                            variant="Cancel"
                            className="bg-gray-500 text-white px-4 py-2 rounded ml-2"
                            label="Cancel"
                          />
                        </>
                      ) : (
                        <>
                          <CustomButton
                            onClick={() => startEditing(state)}
                            variant="edit"
                            label="Edit"
                          />
                          <CustomButton
                            onClick={() => handleDeleteState(state.id)}
                            variant="Delete"
                            className="bg-red-500 text-white px-4 py-2 rounded ml-2"
                            label={saving ? "Please wait" : "Delete"}
                            disabled={saving}
                          />
                        </>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </main>

      {/* Add modal */}
      {isAddModalOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-6">
          <div
            className="absolute inset-0 bg-black opacity-40"
            onClick={() => setAddModalOpen(false)}
          />

          <div
            className="relative bg-white rounded shadow-lg w-full max-w-xl z-10"
            role="dialog"
            aria-modal="true"
          >
            <div className="px-6 py-4 border-b">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold">Add State</h2>
                <button
                  onClick={() => setAddModalOpen(false)}
                  className="text-gray-600 hover:text-gray-900"
                >
                  Close
                </button>
              </div>
            </div>

            <form onSubmit={handleAddState} className="p-6 grid grid-cols-1 gap-4">
              <div className="grid grid-cols-2 gap-6">
                <div className="flex items-center gap-2">
                  <label className="font-bold text-sm w-1/3">
                    Country<span className="text-red-600 ml-1">*</span>
                  </label>
                  <select
                    className="border border-gray-400 px-2 py-1 text-sm flex-1"
                    value={newCountry}
                    onChange={(e) => setNewCountry(e.target.value)}
                    required
                  >
                    <option value="India">India</option>
                  </select>
                </div>

                <div className="flex items-center gap-2">
                  <label className="font-bold text-sm w-1/3">
                    State<span className="text-red-600 ml-1">*</span>
                  </label>
                  <input
                    type="text"
                    value={newStateName}
                    onChange={(e) => setNewStateName(e.target.value)}
                    className="border border-gray-400 px-2 py-1 text-sm flex-1"
                    placeholder="Enter state name"
                    required
                  />
                </div>
              </div>

              <div className="flex justify-end gap-4 mt-4">
                <button
                  type="button"
                  onClick={() => {
                    setAddModalOpen(false);
                    setNewStateName("");
                    setNewCountry("India");
                  }}
                  className="px-4 py-2 rounded border"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="px-4 py-2 rounded bg-blue-600 text-white"
                  disabled={saving}
                >
                  {saving ? "Submitting..." : "Submit"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default StateManagement;
